import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild
} from '@angular/core';

const SVG_CENTER = 128;
const OUTER_RADIUS = 88;
const INNER_RADIUS = 56;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function clockAngleRad(step: number, stepsPerRevolution: number): number {
  return toRad(step * (360 / stepsPerRevolution) - 90);
}

export interface ClockNumber {
  label: string;
  value: number;
  x: number;
  y: number;
  isSelected: boolean;
  isInner: boolean;
}

@Component({
  selector: 'umss-time-picker-clock',
  standalone: true,
  imports: [],
  templateUrl: './time-picker-clock.component.html',
  styleUrl: './time-picker-clock.component.scss'
})
export class UmssTimePickerClockComponent {
  @HostBinding('style.display') display = 'block';

  @Input() hours = 0;
  @Input() minutes = 0;
  @Input() view: 'hours' | 'minutes' = 'hours';

  @Output() hourChange = new EventEmitter<number>();
  @Output() minuteChange = new EventEmitter<number>();
  @Output() viewChange = new EventEmitter<'hours' | 'minutes'>();

  @ViewChild('svgEl') svgEl!: ElementRef<SVGSVGElement>;

  get formattedHours(): string {
    return this.hours.toString().padStart(2, '0');
  }

  get formattedMinutes(): string {
    return this.minutes.toString().padStart(2, '0');
  }

  get clockNumbers(): ClockNumber[] {
    return this.view === 'hours'
      ? this.buildHourNumbers()
      : this.buildMinuteNumbers();
  }

  get minuteTicks(): {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    isMajor: boolean;
  }[] {
    return Array.from({ length: 60 }, (_, i) => {
      const angle = clockAngleRad(i, 60);
      const outerR = 110;
      const innerR = i % 5 === 0 ? 100 : 105;
      return {
        x1: SVG_CENTER + outerR * Math.cos(angle),
        y1: SVG_CENTER + outerR * Math.sin(angle),
        x2: SVG_CENTER + innerR * Math.cos(angle),
        y2: SVG_CENTER + innerR * Math.sin(angle),
        isMajor: i % 5 === 0
      };
    });
  }

  get handEndX(): number {
    return SVG_CENTER + this.handRadius * Math.cos(this.selectedAngleRad);
  }

  get handEndY(): number {
    return SVG_CENTER + this.handRadius * Math.sin(this.selectedAngleRad);
  }

  private get selectedAngleRad(): number {
    if (this.view === 'hours') {
      const pos =
        this.hours === 0 ? 12 : this.hours <= 12 ? this.hours : this.hours - 12;
      return clockAngleRad(pos, 12);
    }
    return clockAngleRad(this.minutes, 60);
  }

  private get handRadius(): number {
    if (this.view === 'hours') {
      return this.hours >= 1 && this.hours <= 12 ? OUTER_RADIUS : INNER_RADIUS;
    }
    return OUTER_RADIUS;
  }

  private buildHourNumbers(): ClockNumber[] {
    const result: ClockNumber[] = [];

    // Outer ring: 1–12
    for (let h = 1; h <= 12; h++) {
      const angle = clockAngleRad(h, 12);
      result.push({
        label: h.toString(),
        value: h,
        x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle),
        isSelected: this.hours === h,
        isInner: false
      });
    }

    // Inner ring: 13–23
    for (let h = 13; h <= 23; h++) {
      const pos = h - 12;
      const angle = clockAngleRad(pos, 12);
      result.push({
        label: h.toString(),
        value: h,
        x: SVG_CENTER + INNER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + INNER_RADIUS * Math.sin(angle),
        isSelected: this.hours === h,
        isInner: true
      });
    }

    // 0 at position of 12 (top), inner ring
    const angle0 = clockAngleRad(12, 12);
    result.push({
      label: '00',
      value: 0,
      x: SVG_CENTER + INNER_RADIUS * Math.cos(angle0),
      y: SVG_CENTER + INNER_RADIUS * Math.sin(angle0),
      isSelected: this.hours === 0,
      isInner: true
    });

    return result;
  }

  private buildMinuteNumbers(): ClockNumber[] {
    const result: ClockNumber[] = [];
    for (let m = 0; m < 60; m += 5) {
      const angle = clockAngleRad(m, 60);
      result.push({
        label: m.toString().padStart(2, '0'),
        value: m,
        x: SVG_CENTER + OUTER_RADIUS * Math.cos(angle),
        y: SVG_CENTER + OUTER_RADIUS * Math.sin(angle),
        isSelected: this.minutes === m,
        isInner: false
      });
    }
    return result;
  }

  onDialKeyDown(event: KeyboardEvent): void {
    const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    if (this.view === 'hours') {
      const delta =
        event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 1 : -1;
      this.hourChange.emit((this.hours + delta + 24) % 24);
    } else {
      const delta =
        event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 1 : -1;
      this.minuteChange.emit((this.minutes + delta + 60) % 60);
    }
  }

  onDialClick(event: MouseEvent): void {
    const svg = this.svgEl.nativeElement;
    const rect = svg.getBoundingClientRect();
    const scaleX = 256 / rect.width;
    const scaleY = 256 / rect.height;
    const svgX = (event.clientX - rect.left) * scaleX;
    const svgY = (event.clientY - rect.top) * scaleY;

    const dx = svgX - SVG_CENTER;
    const dy = svgY - SVG_CENTER;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI + 90;
    const normalized = ((angleDeg % 360) + 360) % 360;

    if (this.view === 'hours') {
      const midRadius = (OUTER_RADIUS + INNER_RADIUS) / 2;
      const isInner = distance < midRadius;
      const rawStep = Math.round(normalized / 30) % 12;

      const value = isInner
        ? rawStep === 0
          ? 0
          : rawStep + 12
        : rawStep === 0
          ? 12
          : rawStep;

      this.hourChange.emit(value);
      this.viewChange.emit('minutes');
    } else {
      // Minutes: implement smart snapping for multiples of 5
      const rawStep = Math.round(normalized / 6) % 60;
      const snappedValue = this.getSnappedMinute(rawStep, normalized);
      this.minuteChange.emit(snappedValue);
    }
  }

  /** Smart snap for minutes: prefer multiples of 5, but allow any value if clicked precisely */
  private getSnappedMinute(
    rawMinute: number,
    normalizedAngleDeg: number
  ): number {
    // Calculate which multiple of 5 is closest
    const nearestFive = Math.round(rawMinute / 5) * 5;
    const anglePerMinute = 360 / 60; // 6 degrees per minute

    // Angle tolerance for snapping: ~3 degrees (0.5 minute width)
    const snapTolerance = 3;
    const angleToNearestFive = Math.abs(
      normalizedAngleDeg - nearestFive * anglePerMinute
    );

    // If angle is within tolerance of a multiple of 5, snap to it
    if (
      angleToNearestFive < snapTolerance ||
      360 - angleToNearestFive < snapTolerance
    ) {
      return nearestFive % 60;
    }

    // Otherwise, return the exact minute value
    return rawMinute;
  }

  switchToHours(): void {
    this.viewChange.emit('hours');
  }

  switchToMinutes(): void {
    this.viewChange.emit('minutes');
  }
}
